import {
  MenuItem,
  Avatar,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  SxProps,
} from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { AccordionArrowPurple, Checkmark, CheckmarkChecked } from 'Images';
import { ReactNode } from 'react';

interface IControlledSelectCheckmark {
  options: string[];
  selected: string[];
  onChange: (event: SelectChangeEvent<string[]>, child: ReactNode) => void;
  label?: string;
  size: 'medium' | 'small';
  width?: string;
  renderValue?: (value: string[]) => ReactNode;
  maxHeight?: number;
  startAdornment?: ReactNode;
  displayEmpty?: boolean;
  ariaLabel?: string;
  disablePortal?: boolean;
  checkboxSx?: SxProps;
  menuPropsSx?: SxProps;
}

export const ControlledSelectCheckmark = ({
  onChange,
  options,
  selected,
  label,
  size,
  width,
  maxHeight,
  renderValue,
  displayEmpty,
  startAdornment,
  ariaLabel,
  disablePortal,
  checkboxSx,
  menuPropsSx,
}: IControlledSelectCheckmark) => {
  return (
    <FormControl
      sx={{
        width: width || '100%',
        bgcolor: '#fff',
        borderRadius: '8px',
        '&&& .MuiOutlinedInput-notchedOutline': {
          borderRadius: '8px',
          borderWidth: '1px',
          borderColor: 'primary.shade_200',
        },

        '&&& .Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'primary.main',
        },

        '& label[data-shrink="false"]': { mt: '-5px' },
      }}
    >
      <InputLabel id='select-label-id'>{label}</InputLabel>
      <Select
        labelId='select-label-id'
        aria-label={ariaLabel}
        label={label}
        multiple
        size={size}
        displayEmpty={displayEmpty}
        value={selected}
        onChange={onChange}
        startAdornment={startAdornment}
        sx={{
          borderRadius: '8px',
          '&&&:hover': {
            fieldset: {
              borderColor: 'primary.main',
            },
          },
          '.MuiSelect-select': {
            fontWeight: 400,
          },
          '& .MuiSelect-icon': {
            cursor: 'pointer',
            top: 'auto',
          },
          '.MuiSelect-iconOpen': {
            top: 'auto',
          },
        }}
        renderValue={(selected) =>
          renderValue
            ? renderValue(selected)
            : `${
                Dictionary[DICTIONARY.MedicalReferrals].search.advancedSearch
                  .chosen
              } ${selected.length}`
        }
        MenuProps={{
          disablePortal: disablePortal,
          sx: {
            ...menuPropsSx,
            zIndex: 1,
          },
          PaperProps: {
            sx: {
              width: startAdornment ? width : undefined,
              color: 'text.secondary',
              borderRadius: '0 0 8px 8px',
              boxShadow: '0px 4px 24px rgba(45, 112, 217, 0.16)',
              padding: '0',
              maxHeight: maxHeight,
              '& > ul': {
                padding: 0,
              },
            },
          },
        }}
        IconComponent={(props) => (
          <Avatar
            {...props}
            src={AccordionArrowPurple}
            alt={Dictionary[DICTIONARY.General].select}
          />
        )}
      >
        {options.map((option: string, index: number) => {
          return (
            <MenuItem
              key={index}
              value={option}
              sx={{
                padding: '6px 16px 6px 10px',
                '&:hover': { bgcolor: 'primary.shade_100' },
                '&.Mui-selected': {
                  fontWeight: '400 !important',
                  bgcolor: 'primary.shade_100',
                },
              }}
            >
              <Checkbox
                checked={selected.indexOf(option) > -1}
                sx={checkboxSx}
                icon={
                  <Avatar
                    alt={''}
                    src={Checkmark}
                    sx={{ width: '24px', height: '24px' }}
                  />
                }
                checkedIcon={
                  <Avatar
                    alt={''}
                    src={CheckmarkChecked}
                    sx={{ width: '24px', height: '24px' }}
                  />
                }
              />
              <ListItemText
                primaryTypographyProps={{
                  fontWeight: {
                    xs: selected.indexOf(option) > -1 ? 700 : 400,
                    sm: selected.indexOf(option) > -1 ? 700 : 400,
                  },
                }}
                primary={option}
              />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};
